import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getPaginationContext} from 'middleware/analytics/selectors'
import {
  getCurrentPage,
  getCurrentPageLength,
  getCurrentPageNumber,
  getGroupedHotelDetails,
  getHasMoreResults,
  getHasPrefetchedResults,
  getHasReachedMaxResults,
  getIsLoadMoreResultsPending,
  getIsSearchCompleted,
  getIsSearchPending,
  getIsWaitingForHotelsData,
  getRequestedOffset,
  getShouldShowNoMoreDeals,
  getTotalCountOfResults
} from 'modules/sapiSearch/selectors'
import {setHasPrefetchedResults, setNextPage} from 'modules/sapiSearch/slice'
import {useSapiSearch} from 'modules/sapiSearch/useSapiSearch'
import {scrollTo} from 'utils'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

export enum PaginationState {
  Loading = 'LOADING',
  HasMore = 'HAS_MORE',
  NoMore = 'NO_MORE',
  Disabled = 'DISABLED'
}

type ReturnType = {
  currentPageNumber: number
  paginationState: PaginationState
  pageHitsOffset: number
  onScrollToTop: () => void
  onLoadMore: () => void
  onPrefetchResults: () => void
}

const scrollToWindowTopSmoothly = () =>
  scrollTo(document.querySelector('html'), 0, 250)

export const usePaginationState = (): PaginationState => {
  const toggle = useToggle()
  const searchIsPending = useSelector(getIsSearchPending)
  const loadMoreResultsIsPending = useSelector(getIsLoadMoreResultsPending)
  const hasMoreResults = useSelector(getHasMoreResults)
  const totalResultsCount = useSelector(getTotalCountOfResults)
  const currentLength = useSelector(getCurrentPageLength)
  const currentPage = useSelector(getCurrentPage)
  const shouldShowNoMoreDeals = useSelector(getShouldShowNoMoreDeals)

  // Added as part of feature-srp-prefetch-pagination-results
  const isWaitingForHotelsData = useSelector(getIsWaitingForHotelsData)
  const {hasPartiallyMatched} = useSelector(getGroupedHotelDetails)
  const hasReachedMaxResults = useSelector(getHasReachedMaxResults)
  const hasPrefetchedResults = useSelector(getHasPrefetchedResults)

  const isHydrated = useIsHydrated()
  const isPrefetchEnabled =
    isHydrated && toggle('feature-srp-prefetch-pagination-results', false, true)

  if (isPrefetchEnabled) {
    if (isWaitingForHotelsData) return PaginationState.Loading
    if (hasPartiallyMatched) return PaginationState.Disabled
    if (shouldShowNoMoreDeals) return PaginationState.NoMore
    if (hasReachedMaxResults && !hasPrefetchedResults)
      return PaginationState.NoMore
    else return PaginationState.HasMore
  }

  if (loadMoreResultsIsPending) return PaginationState.Loading

  if (hasPartiallyMatched) return PaginationState.Disabled

  if (!searchIsPending && hasMoreResults && !shouldShowNoMoreDeals)
    return PaginationState.HasMore

  if (
    (!searchIsPending &&
      !hasMoreResults &&
      totalResultsCount > 0 &&
      currentLength > 0 &&
      currentPage > 0) ||
    shouldShowNoMoreDeals
  ) {
    return PaginationState.NoMore
  }

  return PaginationState.Disabled
}

const usePagination = (): ReturnType => {
  const {loadMoreResults} = useSapiSearch()
  const dispatch = useDispatch()
  const toggle = useToggle()

  const currentPageNumber = useSelector(getCurrentPageNumber)
  const pageHitsOffset = useSelector(getRequestedOffset)
  const paginationContext = useSelector(getPaginationContext)

  const paginationState = usePaginationState()

  // Added as part of feature-srp-prefetch-pagination-results
  const hasPrefetchedResults = useSelector(getHasPrefetchedResults)
  const isSearchCompleted = useSelector(getIsSearchCompleted)
  const shouldShowNoMoreDeals = useSelector(getShouldShowNoMoreDeals)
  const hasReachedMaxResults = useSelector(getHasReachedMaxResults)

  const onLoadMore = useCallback(() => {
    // Added as part of feature-srp-prefetch-pagination-results
    if (hasPrefetchedResults) {
      dispatch(setNextPage())
      dispatch(setHasPrefetchedResults(false))
    } else {
      // In case the user reached the end of the list with a ongoing search or
      // the size of the list is too small, the prefetched results are not available.
      // In this case, we should load more results triggering the pagination - which is the the manual behavior.
      loadMoreResults(true)
    }

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.LoadMoreResultsButton,
        action: Action.Clicked,
        analyticsContext: {
          [AnalyticsContext.PaginationContext]: paginationContext
        },
        payload: {
          hasPrefetchedResults
        }
      })
    )
  }, [loadMoreResults, paginationContext, dispatch, hasPrefetchedResults])

  const onPrefetchResults = useCallback(() => {
    if (!toggle('feature-srp-prefetch-pagination-results', false, true)) return

    if (
      !isSearchCompleted ||
      shouldShowNoMoreDeals ||
      hasPrefetchedResults ||
      hasReachedMaxResults
    )
      return

    // Prefetching
    // Loading more results without triggering the pagination
    loadMoreResults(false)
    dispatch(setHasPrefetchedResults(true))
  }, [
    toggle,
    hasPrefetchedResults,
    isSearchCompleted,
    shouldShowNoMoreDeals,
    hasReachedMaxResults,
    dispatch,
    loadMoreResults
  ])

  return {
    currentPageNumber,
    paginationState,
    pageHitsOffset,
    onScrollToTop: scrollToWindowTopSmoothly,
    onLoadMore,
    onPrefetchResults
  }
}

export default usePagination
